<template>
  <div class="min-h-screen">
    <AppNavbar />
    
    <main class="page-gutter pt-32 pb-20">
      <article class="max-w-4xl mx-auto card">
        <header class="mb-8 pb-6 border-b border-border">
          <h1 class="text-4xl sm:text-5xl font-extrabold mb-4 flex items-center gap-3">
            <svg class="w-10 h-10 text-primary-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
            </svg>
            <span class="gradient-text">User Data Deletion</span>
          </h1>
          <p class="text-muted text-sm">Last updated: October 12, 2025</p>
        </header>

        <section class="space-y-6 text-muted prose prose-invert max-w-none">
          <p>
            You can delete your App to Link account and associated personal data using the option below.
          </p>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">Request by Email</h2>
            <p>
              Email us from the address tied to your App to Link account: <a :href="privacyMailto" class="text-primary-400 hover:text-primary-300 underline">{{ privacyEmail }}</a>
            </p>
            
            <h3 class="text-xl font-semibold text-text mb-3 mt-6">Subject: Delete my App to Link data</h3>
            <p>Include (if available):</p>
            <ul class="list-disc list-inside space-y-2 ml-4">
              <li>Your App to Link username or account email</li>
              <li>(If you signed up via Facebook) your Facebook app-scoped User ID</li>
            </ul>
            <p class="mt-4">
              We will verify ownership and begin deletion within 7 days of verification.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">Facebook Data Deletion Callback</h2>
            <p>
              If you arrived here by clicking "Request data deletion" on Facebook, your request has been recorded. We will process it per the timelines above. If you need a status update, contact <a :href="privacyMailto" class="text-primary-400 hover:text-primary-300 underline">{{ privacyEmail }}</a> with your account email and, if applicable, your Facebook app-scoped User ID.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">What gets deleted</h2>
            <ul class="list-disc list-inside space-y-2 ml-4">
              <li>Profile information, settings, and session data</li>
              <li>Links, posts, pages, and media you created or uploaded to App to Link</li>
              <li>Analytics tied to your account (engagement, clicks, etc.)</li>
              <li>Third-party tokens (e.g., Facebook/Instagram) stored by App to Link</li>
            </ul>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">What we may retain (limited and lawful)</h2>
            <ul class="list-disc list-inside space-y-2 ml-4">
              <li>Minimal records necessary to meet legal, security, fraud-prevention, or accounting obligations (e.g., transaction logs, invoices), retained only as required by law</li>
              <li>Data in encrypted backups until those backups expire (up to 90 days), after which the data is permanently removed</li>
            </ul>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">Third-Party Services</h2>
            <p>
              Upon deletion we revoke/expire tokens and instruct processors to delete data they hold on our behalf where applicable. Some parties (e.g., payment processors) may retain limited records as required by their own legal obligations.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">Timelines</h2>
            <ul class="list-disc list-inside space-y-2 ml-4">
              <li><strong class="text-primary-400">Start:</strong> within 7 days of request/verification</li>
              <li><strong class="text-primary-400">Completion:</strong> within 30 days (backups up to 90 days)</li>
            </ul>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">Appeals & Support</h2>
            <p>
              If you believe your request wasn't handled correctly, reply to our confirmation email or write to <a :href="privacyMailto" class="text-primary-400 hover:text-primary-300 underline">{{ privacyEmail }}</a> with "Deletion Appeal" in the subject.
            </p>
          </div>

          <div class="mt-10">
            <h2 class="text-2xl font-bold text-text mb-4">Contact</h2>
            <p>App to Link — <a href="https://apptolink.com" rel="external nofollow noopener" target="_blank" class="text-primary-400 hover:text-primary-300 underline">apptolink.com</a></p>
            <ul class="list-disc list-inside space-y-2 ml-4">
              <li>Email: <a :href="privacyMailto" class="text-primary-400 hover:text-primary-300 underline">{{ privacyEmail }}</a> (privacy)</li>
              <li>Email: <a :href="infoMailto" class="text-primary-400 hover:text-primary-300 underline">{{ infoEmail }}</a> (general)</li>
            </ul>
            <p class="mt-4">
              For more details about how we collect and use data, see our <router-link to="/privacy-policy" class="text-primary-400 hover:text-primary-300 underline">Privacy Policy</router-link>.
            </p>
          </div>
        </section>
      </article>
    </main>

    <!-- Footer -->
    <footer class="page-gutter py-8 border-t border-border">
      <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
        <p class="text-muted text-sm">&copy; 2024 App to Link. All rights reserved.</p>
        <nav class="flex items-center gap-6 text-sm text-muted">
          <router-link to="/" class="hover:text-text transition">Home</router-link>
          <router-link to="/privacy-policy" class="hover:text-text transition">Privacy Policy</router-link>
          <router-link to="/terms-of-service" class="hover:text-text transition">Terms of Service</router-link>
          <router-link to="/data-deletion" class="hover:text-text transition">Data Deletion</router-link>
          <a :href="infoMailto" class="hover:text-text transition">Contact</a>
        </nav>
      </div>
    </footer>
  </div>
</template>

<script>
import AppNavbar from './AppNavbar.vue';
import emailObfuscation from '@/mixins/emailObfuscation';

export default {
  name: 'DataDeletion',
  components: { AppNavbar },
  mixins: [emailObfuscation]
}
</script>

